import type { AuthUser } from '@/db/auth';

export interface ContentBlockItem {
  type: string;
  id?: string;
  value: Record<string, unknown> | string;
}

export interface AuthStateSnapshot {
  isReady: boolean;
  isAuthenticated: boolean;
  /** True when the session user has approved === true (custom User model). */
  isApproved: boolean;
  /** True when the user has at least one training centre from the session payload. */
  hasTrainingCentre: boolean;
  /** True when at least one linked training centre has accreditation level Licensed. */
  hasLicensedTrainingCentre: boolean;
}

const DEFAULT_LOGIN_REQUIRED_MESSAGE = 'Please sign in to access this form.';

export type AuthorisationRequiredAuthMode =
  | 'account'
  | 'center'
  | 'licensed_training_centre';

/** Process block: public access or same tiers as authorisation_required_message. */
export type ProcessBlockRequiredAuth = 'none' | AuthorisationRequiredAuthMode;

function normaliseAuthorisationRequiredAuth(value: unknown): AuthorisationRequiredAuthMode {
  if (value === 'center') return 'center';
  if (value === 'licensed_training_centre' || value === 'training_centre_account') {
    return 'licensed_training_centre';
  }
  return 'account';
}

export function normaliseProcessBlockRequiredAuth(value: unknown): ProcessBlockRequiredAuth {
  if (value === 'none') return 'none';
  if (value === 'center') return 'center';
  if (value === 'licensed_training_centre' || value === 'training_centre_account') {
    return 'licensed_training_centre';
  }
  if (value === 'account') return 'account';
  return 'none';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function sanitizeFormValue(value: Record<string, unknown>): Record<string, unknown> {
  return {
    form: value.form ?? null,
  };
}

function sanitizeAuthorisationMessageValue(value: Record<string, unknown>): Record<string, unknown> {
  const mode = normaliseAuthorisationRequiredAuth(value.required_auth);
  const body = typeof value.body === 'string' ? value.body : '';
  return {
    body,
    required_auth: mode,
  };
}

function sanitizeProcessBlockValue(value: Record<string, unknown>): Record<string, unknown> {
  const mode = normaliseProcessBlockRequiredAuth(value.required_auth);
  return {
    ...value,
    required_auth: mode,
  };
}

export function authorisationRequirementMet(
  auth: AuthStateSnapshot,
  mode: AuthorisationRequiredAuthMode
): boolean {
  if (mode === 'account') {
    return auth.isAuthenticated && auth.isApproved;
  }
  if (mode === 'center') {
    return auth.isAuthenticated && auth.isApproved && auth.hasTrainingCentre;
  }
  return auth.isAuthenticated && auth.hasLicensedTrainingCentre;
}

/**
 * Next-step CTA for the authorisation-required message block:
 * no account → register; not approved → submissions; otherwise (including approved but
 * missing or non-licensed training centre) → account dashboard.
 */
export type AuthorisationRequiredCtaTarget = 'register' | 'submissions' | 'account';

export function authorisationRequiredCtaTarget(auth: AuthStateSnapshot): AuthorisationRequiredCtaTarget {
  if (!auth.isAuthenticated) {
    return 'register';
  }
  if (!auth.isApproved) {
    return 'submissions';
  }
  return 'account';
}

/** Builds the auth snapshot used by resolveContentBlocksForAuth from the headless session user. */
/** Options for {@link resolveContentBlocksForAuth} (e.g. Wagtail draft preview). */
export type ResolveContentBlocksForAuthOptions = {
  /**
   * When true (Wagtail page preview with token), tier-gated process blocks stay visible even if
   * the session is not ready yet; the Process block also bypasses auth in the preview shell.
   */
  wagtailContentPreview?: boolean;
};

export function authSnapshotFromSession(
  isReady: boolean,
  isAuthenticated: boolean,
  user: AuthUser | null
): AuthStateSnapshot {
  const centres = user?.training_centres?.length
    ? user.training_centres
    : user?.organisations ?? [];
  const centreList = Array.isArray(centres) ? centres : [];
  const hasLicensedTrainingCentre = centreList.some(
    (c) => Boolean(c && typeof c === 'object' && (c as { licensed?: boolean }).licensed === true)
  );
  return {
    isReady,
    isAuthenticated,
    isApproved: user?.approved === true,
    hasTrainingCentre: centreList.length > 0,
    hasLicensedTrainingCentre,
  };
}

export function resolveContentBlocksForAuth(
  blocks: ContentBlockItem[],
  auth: AuthStateSnapshot,
  options?: ResolveContentBlocksForAuthOptions
): ContentBlockItem[] {
  const result: ContentBlockItem[] = [];
  const preview = Boolean(options?.wagtailContentPreview);
  for (const block of blocks) {
    if (block.type === 'form') {
      const value = asRecord(block.value);
      if (!value) {
        result.push(block);
        continue;
      }

      const loginRequired = Boolean(value.login_required);
      if (!loginRequired) {
        result.push({
          ...block,
          value: sanitizeFormValue(value),
        });
        continue;
      }

      if (!auth.isReady) {
        continue;
      }

      if (auth.isAuthenticated) {
        result.push({
          ...block,
          value: sanitizeFormValue(value),
        });
        continue;
      }

      const unauthenticatedBehavior =
        value.unauthenticated_behavior === 'message' ? 'message' : 'hide';
      if (unauthenticatedBehavior === 'hide') {
        continue;
      }

      const configuredMessage =
        typeof value.unauthenticated_message === 'string'
          ? value.unauthenticated_message.trim()
          : '';
      result.push({
        ...block,
        type: 'protected_message',
        value: {
          message: configuredMessage || DEFAULT_LOGIN_REQUIRED_MESSAGE,
        },
      });
      continue;
    }

    if (block.type === 'process_block') {
      const value = asRecord(block.value);
      if (!value) {
        result.push(block);
        continue;
      }
      const mode = normaliseProcessBlockRequiredAuth(value.required_auth);
      if (mode === 'none') {
        result.push({
          ...block,
          value: sanitizeProcessBlockValue(value),
        });
        continue;
      }
      if (!auth.isReady && !preview) {
        continue;
      }
      result.push({
        ...block,
        value: sanitizeProcessBlockValue(value),
      });
      continue;
    }

    if (block.type === 'authorisation_required_message') {
      const value = asRecord(block.value);
      if (!value) {
        continue;
      }
      if (!auth.isReady) {
        continue;
      }
      const mode = normaliseAuthorisationRequiredAuth(value.required_auth);
      if (authorisationRequirementMet(auth, mode)) {
        continue;
      }
      result.push({
        ...block,
        value: sanitizeAuthorisationMessageValue(value),
      });
      continue;
    }

    result.push(block);
  }
  return result;
}
